/**
 * Dependents Warning Dialog
 *
 * Modal dialog shown when a row cannot be deleted due to
 * referential integrity constraints (409 Conflict from backend).
 * Lists the dependent tables and their record counts.
 */

import React from "react";
import {
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  List,
  ListItem,
  ListItemContent,
  Chip,
} from "@mui/joy";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";

export interface DependentRecord {
  table_name: string;
  count: number;
  on_delete: string;
}

interface DependentsWarningDialogProps {
  open: boolean;
  onClose: () => void;
  dependents: DependentRecord[];
}

export default function DependentsWarningDialog({
  open,
  onClose,
  dependents,
}: DependentsWarningDialogProps) {
  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog variant="outlined" role="alertdialog">
        <DialogTitle>
          <WarningRoundedIcon />
          Cannot delete this record
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            This record has dependent records in other tables. You must remove
            or reassign those records before this one can be deleted.
          </Typography>
          <List size="sm" sx={{ "--ListItem-paddingY": "4px" }}>
            {dependents.map((dep, index) => (
              <ListItem key={index}>
                <ListItemContent>
                  <Typography level="body-sm" sx={{ fontWeight: "md" }}>
                    {dep.table_name}
                  </Typography>
                </ListItemContent>
                <Chip size="sm" variant="soft" color="warning">
                  {dep.count} {dep.count === 1 ? "record" : "records"}
                </Chip>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button variant="solid" color="neutral" onClick={onClose}>
            Close
          </Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
}
